import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
    baseURL: API_URL,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 — нет токена, 403 — недействительный/просроченный токен.
    // В обоих случаях сбрасываем локальный токен, чтобы пользователя вернуло на логин.
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

export const getSignals = async (filters = {}) => {
  try {
    const response = await api.get('/api/signals', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching signals:', error);
    throw error;
  }
};

export const getSignalHistory = async (oltIp, portNumber, period = '1month') => {
  try {
    const response = await api.get(`/api/signals/history/${oltIp}/${portNumber}`, {
      params: { period }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching signal history:', error);
    throw error;
  }
};

export const getOltIps = async () => {
  try {
    const response = await api.get('/api/olt-ips');
    return response.data;
  } catch (error) {
    console.error('Error fetching OLT IPs:', error);
    throw error;
  }
};

export const getLastActiveDate = async (oltIp, portNumber) => {
  try {
    const response = await api.get(`/api/signals/last-active/${oltIp}/${portNumber}`);
    return response.data.lastActiveDate;
  } catch (error) {
    console.error(`Error fetching last active date for ${oltIp}:${portNumber}:`, error);
    return null;
  }
};

export const getLastActiveDatesBulk = async (ports) => {
  if (!ports?.length) return {};
  try {
    const response = await api.post('/api/signals/last-active/bulk', { ports });
    return response.data.dates || {};
  } catch (error) {
    console.error('Error fetching bulk last active dates:', error);
    return {};
  }
};

export const getOnuStatusHistory = async (oltIp, portId, period = '1month') => {
  try {
    console.log(`Запрос истории статуса ONU для ${oltIp}:${portId}, период: ${period}`);
    const response = await api.get(`/api/onu-status/${oltIp}/${portId}`, {
      params: { period }
    });
    console.log('Ответ с историей статуса ONU:', response);
    console.log(`Получено ${response.data.length} записей о статусе ONU`);
    return response.data;
  } catch (error) {
    console.error('Ошибка при получении истории статуса ONU:', error);
    throw error;
  }
};

export const getCurrentSignals = async (oltIp, portNumber) => {
  try {
    // Удалите или закомментируйте эту строку
    // console.log(`Запрос текущих показаний сигнала для ${oltIp}:${portNumber}`);
    
    // Удалите параметр debug=true, чтобы не получать отладочную информацию
    // const url = `/api/current-signals/${oltIp}/${portNumber}?debug=true`;
    const url = `/api/current-signals/${oltIp}/${portNumber}`;
    
    // Удалите или закомментируйте эту строку
    // console.log(`Отправка запроса на URL: ${url}`);
    
    const response = await api.get(url);
    
    // Удалите или закомментируйте эти строки
    // console.log(`Получен ответ от сервера:`, response);
    // console.log(`Данные ответа:`, response.data);
    
    return response.data;
  } catch (error) {
    console.error('Ошибка при получении текущих показаний сигнала:', error);
    throw error;
  }
};

export const getPortDescriptions = async (oltIps, portIds) => {
  try {
    console.log('Запрос описаний портов для:', { oltIps, portIds });
    // Преобразуем массивы в строки для передачи в параметрах запроса
    const oltIpsParam = oltIps.join(',');
    const portIdsParam = portIds.join(',');
    
    const response = await api.get('/api/port-descriptions', {
      params: { 
        olt_ips: oltIpsParam,
        port_ids: portIdsParam
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching port descriptions:', error);
    return [];
  }
};

export const getCurrentSignalsBulk = async (oltIps, portIds) => {
  try {
    console.log('Запрос актуальных значений сигнала для:', { oltIps, portIds });
    // Преобразуем массивы в строки для передачи в параметрах запроса
    const oltIpsParam = oltIps.join(',');
    const portIdsParam = portIds.join(',');
    
    const response = await api.get('/api/current-signals-bulk', {
      params: { 
        olt_ips: oltIpsParam,
        port_ids: portIdsParam
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching current signals:', error);
    return { signals: {}, count: 0 };
  }
};

export const getSpeedHistoryByPort = async (oltIp, portNumber, days = 7) => {
  try {
    const response = await api.get(`/api/speed/by-port/${oltIp}/${portNumber}`, {
      params: { days }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching speed history by port:', error);
    throw error;
  }
};

// Активные аварии/падения PON (детектор переходов UP→DOWN с LOS за окно).
// Возвращает { branches: [...], window_minutes, threshold, generated_at }.
export const getActiveAlerts = async (minutes = 15, onlySevere = false) => {
  try {
    const response = await api.get('/api/alerts/active', {
      params: { minutes }
    });
    const data = response.data || { branches: [] };
    if (onlySevere) {
      return { ...data, branches: (data.branches || []).filter(b => b.severity) };
    }
    return data;
  } catch (error) {
    console.error('Error fetching active alerts:', error);
    return { branches: [], error: error.message };
  }
};

// Тестовое сообщение в Max (админка). Требует роль admin на бэкенде.
export const sendAlertTest = async () => {
  const response = await api.post('/api/admin/alert/test');
  return response.data;
};

export default api; 